import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { listBills } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, FilterBar, Button } from '../components/shared/ui'
import { currency, date } from '../lib/format'
import { useServerList } from '../lib/list'
import type { Filter } from '../lib/list'
import type { Bill } from '../types'

const PAGE_SIZE = 25

function PaymentStatus({ b }: { b: Bill }) {
  const total = Number(b.invoice_total ?? 0)
  const balance = Number(b.remaining_balance ?? 0)
  if (total === 0) return <span className="text-gray-400 text-xs">—</span>
  if (balance <= 0.009)
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">Paid</span>
  if (balance < total - 0.009)
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Partial</span>
  return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Unpaid</span>
}

export default function Bills() {
  const navigate = useNavigate()
  const [hasBalance, setHasBalance] = useState(false)
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort } =
    useServerList<Bill>(listBills, { pageSize: PAGE_SIZE, defaultSort: { column: 'bill_date', dir: 'desc' } })

  const applyFilters = (barFilters: Filter[]) => {
    const extra: Filter[] = hasBalance ? [{ column: 'remaining_balance', op: 'gte', value: 0.01 }] : []
    setFilters([...extra, ...barFilters])
  }

  return (
    <div>
      <PageHeader title="Job Invoices" subtitle={`${count.toLocaleString()} landscaping invoices`}
        actions={<Button onClick={() => navigate('/bills/new')}><Plus size={16} /> New Job Invoice</Button>} />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by bill code or customer…" />
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={hasBalance} onChange={e => {
                const next = e.target.checked
                setHasBalance(next)
                setFilters(next ? [{ column: 'remaining_balance', op: 'gte', value: 0.01 }] : [])
              }} className="h-4 w-4 rounded border-gray-300" />
              <span className="text-gray-700 font-medium">Has outstanding balance</span>
            </label>
          </div>
          <FilterBar
            onChange={applyFilters}
            fields={[
              { key: 'date', label: 'Invoice Date', column: 'bill_date', type: 'daterange' },
              { key: 'spring', label: 'Spring Cleanup', column: 'is_spring_cleanup', type: 'boolean' },
            ]}
          />
        </div>
        {loading ? (
          <Spinner label="Loading bills…" />
        ) : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={b => navigate(`/bills/${b.id}`)}
              columns={[
                { key: 'bill_code', header: 'Code', render: b => <span className="font-medium text-gray-900">{b.bill_code || '—'}</span>, sortKey: 'bill_code' },
                { key: 'customer_name', header: 'Customer', render: b => b.customer_name || <span className="text-gray-400">—</span>, sortKey: 'customer_name' },
                { key: 'bill_date', header: 'Date', render: b => date(b.bill_date), sortKey: 'bill_date' },
                { key: 'invoice_total', header: 'Total', render: b => currency(b.invoice_total), className: 'text-right', sortKey: 'invoice_total' },
                { key: 'remaining_balance', header: 'Balance', render: b => currency(b.remaining_balance), className: 'text-right', sortKey: 'remaining_balance' },
                { key: 'status', header: 'Status', render: b => <PaymentStatus b={b} /> },
                { key: 'gross_profit', header: 'Gross Profit', render: b => currency(b.gross_profit), className: 'text-right', sortKey: 'gross_profit' },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  )
}
